import React, { useState, useEffect, useCallback } from 'react';
import recruitingService from '../utils/recruitingService';
import './RecentHiresTable.css';

/**
 * RecentHiresTable Component
 * Searchable, sortable table of recent hires
 */
function RecentHiresTable({ filters }) {
    const [hires, setHires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('startDate');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetch hires
    const fetchHires = useCallback(async () => {
        setLoading(true);
        try {
            const response = await recruitingService.getRecentHires({
                limit: 100,
                ...filters
            });
            if (response.success && response.data) {
                setHires(response.data);
            }
        } catch (err) {
            console.error('Error fetching recent hires:', err);
            setHires([]);
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchHires();
    }, [fetchHires]);

    // Filter by search term
    const filteredHires = hires.filter(h => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            h.employeeName?.toLowerCase().includes(search) ||
            h.jobTitle?.toLowerCase().includes(search) ||
            h.department?.toLowerCase().includes(search) ||
            h.recruiter?.toLowerCase().includes(search) ||
            h.source?.toLowerCase().includes(search)
        );
    });

    // Sort hires
    const sortedHires = [...filteredHires].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        // Handle null values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        // Date fields
        if (sortField === 'startDate') {
            aVal = aVal ? new Date(aVal).getTime() : 0;
            bVal = bVal ? new Date(bVal).getTime() : 0;
        }

        // Number fields
        if (sortField === 'daysToHire') {
            aVal = parseInt(aVal) || 0;
            bVal = parseInt(bVal) || 0;
        }

        if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }

        if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
    });

    // Pagination
    const totalPages = Math.ceil(sortedHires.length / itemsPerPage);
    const paginatedHires = sortedHires.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Handle sort click
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Get sort indicator
    const getSortIndicator = (field) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        if (status === 'Completed') return 'status-completed';
        if (status === 'In Progress') return 'status-in-progress';
        return 'status-pending';
    };

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="recent-hires-table table-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="recent-hires-table">
            <div className="table-header">
                <div className="table-title-section">
                    <h3 className="chart-title">Recent Hires</h3>
                    <span className="table-count">{filteredHires.length} hires</span>
                </div>
                <div className="table-search">
                    <input
                        type="text"
                        placeholder="Search hires..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="search-input"
                    />
                </div>
            </div>

            <div className="table-wrapper">
                <table className="hires-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('employeeName')}>
                                Name{getSortIndicator('employeeName')}
                            </th>
                            <th onClick={() => handleSort('startDate')}>
                                Start Date{getSortIndicator('startDate')}
                            </th>
                            <th onClick={() => handleSort('jobTitle')}>
                                Job Title{getSortIndicator('jobTitle')}
                            </th>
                            <th onClick={() => handleSort('department')}>
                                Department{getSortIndicator('department')}
                            </th>
                            <th onClick={() => handleSort('recruiter')}>
                                Recruiter{getSortIndicator('recruiter')}
                            </th>
                            <th onClick={() => handleSort('source')}>
                                Source{getSortIndicator('source')}
                            </th>
                            <th onClick={() => handleSort('daysToHire')}>
                                Days to Hire{getSortIndicator('daysToHire')}
                            </th>
                            <th onClick={() => handleSort('phaseStatus')}>
                                Status{getSortIndicator('phaseStatus')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedHires.length > 0 ? (
                            paginatedHires.map((hire) => (
                                <tr key={hire.id}>
                                    <td className="employee-name">{hire.employeeName || 'N/A'}</td>
                                    <td>{formatDate(hire.startDate)}</td>
                                    <td>{hire.jobTitle || '-'}</td>
                                    <td>{hire.department || '-'}</td>
                                    <td>{hire.recruiter || '-'}</td>
                                    <td>{hire.source || '-'}</td>
                                    <td className="days-to-hire">
                                        {hire.daysToHire !== null ? (
                                            <span className={hire.daysToHire <= 45 ? 'days-good' : hire.daysToHire <= 60 ? 'days-warning' : 'days-slow'}>
                                                {hire.daysToHire}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusBadgeClass(hire.phaseStatus)}`}>
                                            {hire.phaseStatus || 'Pending'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="no-data">
                                    {searchTerm ? 'No hires match your search' : 'No recent hires'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="table-pagination">
                    <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

export default RecentHiresTable;
